import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { NavItem, NAV_ITEMS_PRE_ONBOARDING, NAV_ITEMS_POST_ONBOARDING } from './side-nav.config';
import { UserSessionService } from '@core/services/user-session.service';
import { StorageKey } from '@core/storage-keys.enum';

@Component({
    selector: 'app-side-nav',
    templateUrl: './side-nav.component.html',
    styleUrls: ['./side-nav.component.scss'],
})
export class SideNavComponent implements OnInit {
    @Input() mobileOpen = false;
    @Output() mobileOpenChange = new EventEmitter<boolean>();

    readonly sectionTitle = 'Products';
    readonly aiAgentsRoute = '/ai-agents';
    navItems: NavItem[] = [];
    showEditSetupFooter = false;

    isCollapsed = localStorage.getItem(StorageKey.SideNavbarMinimized) === 'true';

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private userSessionService: UserSessionService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.updateNavItems();

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.updateNavItems());
    }

    updateNavItems(): void {
        const onboardingCompleted = this.userSessionService.isOnboardingCompleted();
        const isEditSetupMode = this.userSessionService.isEditSetupMode();
        this.navItems = onboardingCompleted && !isEditSetupMode ? NAV_ITEMS_POST_ONBOARDING : NAV_ITEMS_PRE_ONBOARDING;
        this.showEditSetupFooter =
            onboardingCompleted && !isEditSetupMode && this.router.url.startsWith(this.aiAgentsRoute);

        if (onboardingCompleted) {
            this.navItems.forEach(item => {
                if (item.children?.length) {
                    item.expanded = this.hasActiveChild(item);
                }
            });
        }
    }

    navigateToEditSetup(): void {
        this.closeMobileNav();
        this.userSessionService.enterEditSetupMode();
        this.router.navigate(['/onboarding/setup']);
    }

    toggleGroup(item: NavItem, event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        item.expanded = !item.expanded;
    }

    toggleCollapse(): void {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem(StorageKey.SideNavbarMinimized, String(this.isCollapsed));
    }

    closeMobileNav(): void {
        this.mobileOpenChange.emit(false);
    }

    hasActiveChild(item: NavItem): boolean {
        return item.children?.some(child => this.router.url.startsWith(child.route)) ?? false;
    }

    isChildActive(child: NavItem): boolean {
        return this.router.url.startsWith(child.route);
    }

    getIconClass(item: NavItem, isActive: boolean): string {
        if (isActive && item.activeIcon) {
            return item.activeIcon;
        }
        return item.icon;
    }
}
